import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { putSetting, deleteSetting } from '../../api';
import { qk } from '../../api/queryKeys';
import { useCapabilities } from '../../hooks/useCapabilities';
import { errorMessage } from '../../lib/api';
import { Badge } from '../../components/Badge';
import { Callout } from '../../components/Callout';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Flash } from '../../components/Flash';
import { Spinner } from '../../components/Spinner';

export interface SecretSpec {
  key: string;
  title: string;
  description: string;
  placeholder: string;
  /** Rendered as a "Required" badge — reserve for auth modes with no fallback. */
  required?: boolean;
  warn?: boolean;
  /** Ends the Remove confirmation message — names what breaks once the value is gone. */
  removeConsequence: string;
}

/** A labeled group of settings cards, giving the page top-level structure. */
export function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        <p className="mt-0.5 text-xs text-ink-faint">{description}</p>
      </div>
      {children}
    </section>
  );
}

/** The shared configured/not-set pill for every credential card. */
export function PresenceBadge({
  configured,
  onLabel = 'Configured',
  offLabel = 'Not set',
}: {
  configured: boolean;
  onLabel?: string;
  offLabel?: string;
}) {
  return (
    <Badge tone={configured ? 'success' : 'neutral'} size="md" dot>
      {configured ? onLabel : offLabel}
    </Badge>
  );
}

/**
 * One workspace token: presence badge plus the set/replace/remove controls.
 *
 * Writing a workspace token requires `workspace:manage` (org owner or platform
 * admin) — the same gate `SettingsController` enforces. A member without it
 * still sees WHETHER the token is configured (useful context, and it leaks
 * nothing — presence is all the API returns anyway) but gets no form, so the
 * UI never offers a control the server would 403. Gating lives here, at the
 * single choke point every token field renders through, rather than in each
 * caller where a future field could forget it.
 */
export function SecretField({
  spec,
  configured,
}: {
  spec: SecretSpec;
  configured: boolean;
}) {
  const { can } = useCapabilities();
  const mayManage = can('workspace:manage');
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: qk.settings });

  const save = useMutation({
    mutationFn: () => putSetting(spec.key, value.trim()),
    onSuccess: () => {
      setValue('');
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteSetting(spec.key),
    onSuccess: () => {
      setConfirmRemove(false);
      invalidate();
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (value.trim()) save.mutate();
  };

  return (
    <div className="card p-5">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-ink">{spec.title}</h3>
          {spec.required && (
            <Badge tone="warning" size="sm">
              Required
            </Badge>
          )}
        </div>
        <PresenceBadge configured={configured} />
      </div>

      <p
        className={`mb-3 text-xs ${spec.warn ? 'text-warning' : 'text-ink-muted'}`}
      >
        {spec.description}
      </p>

      {!mayManage ? (
        <p className="text-xs text-ink-muted">
          Only a workspace owner can change this token.
        </p>
      ) : (
      <form
        onSubmit={onSubmit}
        className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-start"
      >
        <div className="flex-1">
          <input
            type="password"
            className="input font-mono"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={configured ? 'Enter a new value to replace…' : spec.placeholder}
            autoComplete="off"
          />
          {save.isError && (
            <Callout tone="danger" className="mt-2">
              {errorMessage(save.error, 'Could not update setting')}
            </Callout>
          )}
          {save.isSuccess && !value && (
            <div className="mt-1">
              <Flash onDone={() => save.reset()}>Saved.</Flash>
            </div>
          )}
        </div>
        <button
          type="submit"
          className="btn-primary whitespace-nowrap"
          disabled={save.isPending || !value.trim()}
        >
          {save.isPending && <Spinner className="h-3.5 w-3.5" />}
          {configured ? 'Update' : 'Save'}
        </button>
        {configured && (
          <button
            type="button"
            className="btn-danger-ghost whitespace-nowrap"
            onClick={() => setConfirmRemove(true)}
          >
            Remove
          </button>
        )}
      </form>
      )}

      <ConfirmDialog
        open={confirmRemove}
        title={`Remove ${spec.title}`}
        message={`Remove the ${spec.title}? ${spec.removeConsequence}`}
        confirmLabel="Remove"
        busy={remove.isPending}
        error={
          remove.isError
            ? errorMessage(remove.error, 'Could not remove the setting')
            : null
        }
        onConfirm={() => remove.mutate()}
        onClose={() => {
          setConfirmRemove(false);
          remove.reset();
        }}
      />
    </div>
  );
}
